import * as React from 'react'
import styled, { css } from 'styled-components'

import * as entity from '@boutique/entities'
import * as utils from '@boutique/utils'
import * as plume from '@ulule/owl-kit-components/next'

import { type SelectedVariant } from '../hooks'

import { Description } from './Description'
import { Shipping } from './Shipping'

export type TabInformationProps = {
  activeTabIndex?: number
  product: entity.product.Product
  selectedVariant: SelectedVariant
  setActiveTabIndex: (index: number) => void
}

export function TabInformation({
  activeTabIndex,
  product,
  selectedVariant,
  setActiveTabIndex,
}: TabInformationProps): React.ReactElement<TabInformationProps> {
  const activeTabIndexRef = React.useRef(0)

  if (activeTabIndex !== undefined) {
    activeTabIndexRef.current = activeTabIndex
  }
  const deliveryWarningDeliveryMessage = utils.shipping.getDeliveryWarningMessage(
    product.deliveryExtra,
    product.productionDays,
  )

  return (
    <>
      <TabMenu>
        <plume.Tab active={activeTabIndexRef.current === 0} onClick={() => setActiveTabIndex(0)}>
          <TabItem asDesktop="XS" active={activeTabIndexRef.current === 0}>
            Caractéristiques
          </TabItem>
        </plume.Tab>
        <plume.Tab active={activeTabIndexRef.current === 1} onClick={() => setActiveTabIndex(1)}>
          <TabItem asDesktop="XS" active={activeTabIndexRef.current === 1}>
            Livraison et retours
          </TabItem>
        </plume.Tab>
      </TabMenu>
      <ThinLine />
      {activeTabIndexRef.current === 0 && (
        <TabSection>
          <Description description={product.description} />
        </TabSection>
      )}
      {activeTabIndexRef.current === 1 && (
        <TabSection>
          {deliveryWarningDeliveryMessage && (
            <DeliveryDelay type="info">{deliveryWarningDeliveryMessage}</DeliveryDelay>
          )}
          <Shipping product={product} selectedVariant={selectedVariant} />
        </TabSection>
      )}
    </>
  )
}

const TabSection = styled.section`
  padding: 24px 0;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    padding: 32px 30px 32px 70px;
  }
`

const TabMenu = styled(plume.TabMenu)`
  justify-content: flex-start;
  margin-top: 48px;
  white-space: nowrap;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    padding-right: 70px;
    padding-left: 70px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-top: 62px;

    ${plume.Tab} {
      margin-right: 58px;
      padding: 0;
    }
  }
`

const ThinLine = styled.div`
  opacity: 0.5;
  background-color: ${plume.COLORS.GREY_SHADE_7};
  width: 100vw;
  position: absolute;
  left: 0;
  height: 1px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    position: relative;
    margin-left: 32px;
    width: initial;
  }
`

const TabItem = styled(plume.styles.heading.XXXS)<{ active: boolean }>`
  font-weight: 600;
  padding-bottom: 16px;
  cursor: pointer;

  ${({ active }) => {
    if (!active) {
      return css`
        opacity: 0.5;
      `
    }
  }}
`

const DeliveryDelay = styled(plume.Callout)`
  display: block;
  transition: none;
  margin-bottom: 30px;
`
